/**
 * Product helper utilities shared across Stocktake wizard panels, the count
 * entry UI, and the offline count app. Centralised here so the yield /
 * "expected servings" hint renders identically everywhere.
 */

/**
 * Return the default yield row for a product (prefers `is_default`,
 * falls back to the first row). Works with both web-API shapes and
 * offline-package shapes (field names preserved).
 */
export function getDefaultYield(product) {
  if (!product || !Array.isArray(product.yields) || product.yields.length === 0) return null;
  return product.yields.find(y => y.is_default) || product.yields[0];
}

/**
 * Return a readable "yield hint" string for a product, or null when no
 * yield is populated. Examples:
 *   Keg with nominal_servings_per_unit=88 → "Yield: 88 pints/keg"
 *   Bottle with servings_per_unit=25 (ml/serving=25) → "Yield: 25 × 25ml/bottle"
 *   Nothing populated → null
 *
 * The hint is designed for inline display next to the product name on the
 * delivery/credit product picker, the closing-stock count row, and the
 * offline count app. All three surfaces use this helper so a change here
 * propagates everywhere.
 */
export function getYieldHint(product) {
  const y = getDefaultYield(product);
  if (!y) return null;

  // Prefer the nominal (target / "yield 88") figure if populated; fall back
  // to the theoretical servings_per_unit when the target is blank.
  const servings = y.nominal_servings_per_unit != null && y.nominal_servings_per_unit !== ''
    ? Number(y.nominal_servings_per_unit)
    : (y.servings_per_unit != null && y.servings_per_unit !== ''
      ? Number(y.servings_per_unit)
      : null);
  if (!servings || !isFinite(servings) || servings <= 0) return null;

  const ml = y.nominal_ml_per_serving != null && y.nominal_ml_per_serving !== ''
    ? Number(y.nominal_ml_per_serving)
    : (y.ml_per_serving != null && y.ml_per_serving !== ''
      ? Number(y.ml_per_serving)
      : null);

  // Unit label: prefer a clean yield_type (e.g. "pint", "shot", "measure").
  // If the stored yield_type is an internal identifier like
  // "servings_per_unit" (underscored / snake_case), fall back to a
  // ml-derived label, otherwise use a generic "servings".
  let servingLabel = (y.yield_type || '').toString().trim().toLowerCase();
  if (servingLabel.includes('_') || servingLabel === '') {
    servingLabel = '';
  }
  if (!servingLabel) {
    if (ml && Math.abs(ml - 568) < 1) servingLabel = 'pint';
    else if (ml && Math.abs(ml - 25) < 1) servingLabel = 'shot';
    else if (ml && Math.abs(ml - 175) < 1) servingLabel = 'glass';
    else if (ml && Math.abs(ml - 125) < 1) servingLabel = 'glass';
    else servingLabel = 'serving';
  }

  // Pluralise
  const plural = servings === 1 ? servingLabel : (servingLabel.endsWith('s') ? servingLabel : `${servingLabel}s`);

  // Source unit label: first non-case unit_type, else "unit"
  let sourceUnit = 'unit';
  if (Array.isArray(product.units)) {
    const defUnit = product.units.find(u => u.is_default_count_unit)
      || product.units.find(u => u.is_base_stock_unit)
      || product.units.find(u => u.unit_type && u.unit_type !== 'case')
      || product.units[0];
    if (defUnit && defUnit.unit_type) sourceUnit = defUnit.unit_type;
  }

  const servingsFmt = Number.isInteger(servings) ? String(servings) : servings.toFixed(2);
  const mlStr = ml ? ` × ${Number.isInteger(ml) ? ml : ml.toFixed(0)}ml` : '';

  return `Yield: ${servingsFmt}${mlStr} ${plural}/${sourceUnit}`;
}
